import { useEffect, useState } from "react";
import RichTextEditor, { RichTextDisplay } from "./RichTextEditor";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { writeActivityEvent } from "./adminActivity";
import {
  getAllPromptsForTag,
  getUserResponseForPrompt,
  ensureWeeklyQuestResponse,
  getGroupResponsesForPrompt,
  getGroupResponsesForPromptAdmin,
  markPromptMissedIfNeeded,
  addWeeklyQuestComment,
  getCommentsForResponse,
  updateWeeklyQuestResponse,
  updateWeeklyQuestComment,
  toggleWeeklyQuestReaction,
  getReactionsForTarget,
  submitWeeklyQuest,
  saveWeeklyQuestDraft,
} from "./weeklyQuests";

export default function AssignmentsScreen({ currentUser, isAdmin, onXpAwarded }) {
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [myResponse, setMyResponse] = useState(null);
  const [groupResponses, setGroupResponses] = useState([]);
  const [saveStatus, setSaveStatus] = useState("Saved");
  const [commentsByResponse, setCommentsByResponse] = useState({});
const [commentDrafts, setCommentDrafts] = useState({});
const [editingResponseId, setEditingResponseId] = useState(null);
const [editingResponseText, setEditingResponseText] = useState("");
const [editingCommentId, setEditingCommentId] = useState(null);
const [editingCommentText, setEditingCommentText] = useState("");
const [reactionsByTarget, setReactionsByTarget] = useState({});
const [expandedCommentsByResponse, setExpandedCommentsByResponse] = useState({});
const [openReactionPickerKey, setOpenReactionPickerKey] = useState(null);
const [showAllPrompts, setShowAllPrompts] = useState(true);
const [resolvedProgramTag, setResolvedProgramTag] = useState("");

  useEffect(() => {
    loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.programTag]);

  

  async function loadAssignments() {
    if (!currentUser?.uid) {
      setPrompts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let resolvedTag = "";
      const userSnap = await getDoc(doc(db, "users", currentUser.uid));
      resolvedTag = String(userSnap.data()?.programTag || currentUser.programTag || "").trim();

      if (resolvedTag) {
        setResolvedProgramTag(resolvedTag);
      }

      // Admin sees all prompts; regular users only see prompts for their tag
      let promptList = [];
      if (isAdmin) {
        const snap = await getDocs(collection(db, "weeklyQuestPrompts"));
        promptList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        promptList.sort((a, b) => (b.startAt?.toDate?.()?.getTime?.() || 0) - (a.startAt?.toDate?.()?.getTime?.() || 0));
      } else {
        if (!resolvedTag) {
          setPrompts([]);
          return;
        }
        promptList = await getAllPromptsForTag(resolvedTag);

        for (const prompt of promptList) {
          try {
            await markPromptMissedIfNeeded(prompt, currentUser);
          } catch (err) {
            console.warn("markPromptMissedIfNeeded skipped:", err);
          }
        }
      }

      const enriched = [];
      for (const prompt of promptList) {
        const response = await getUserResponseForPrompt(prompt.id, currentUser.uid);
        enriched.push({
          ...prompt,
          myResponse: response,
          groupResponseCount: 0,
        });
      }

      setPrompts(enriched);
    } catch (error) {
      console.error("Error loading assignments:", error);
    } finally {
      setLoading(false);
    }
  }

async function loadCommentsForResponse(responseId) {
  try {
    const comments = await getCommentsForResponse(responseId);

    setCommentsByResponse((prev) => ({
      ...prev,
      [responseId]: comments,
    }));
  } catch (error) {
    console.error("Error loading comments:", error);
  }
}

async function loadReactionsForTarget(targetType, targetId) {
  try {
    const reactions = await getReactionsForTarget(targetType, targetId);

    setReactionsByTarget((prev) => ({
      ...prev,
      [`${targetType}_${targetId}`]: reactions,
    }));
  } catch (error) {
    console.error("Error loading reactions:", error);
  }
}

async function loadReactionsForResponseTree(response) {
  if (!response?.id) return;

  await loadReactionsForTarget("response", response.id);

  const comments = await getCommentsForResponse(response.id);

  setCommentsByResponse((prev) => ({
    ...prev,
    [response.id]: comments,
  }));

  for (const comment of comments) {
    await loadReactionsForTarget("comment", comment.id);
  }
}

function startEditingResponse(response) {
  setEditingResponseId(response.id);
  setEditingResponseText(response.responseText || "");
}

function cancelEditingResponse() {
  setEditingResponseId(null);
  setEditingResponseText("");
}

async function saveDraft(response) {
  if (!response?.id) return;
  try {
    setSaveStatus("Saving...");
    await saveWeeklyQuestDraft(response.id, editingResponseText, response.isPrivate ?? false);
    setSaveStatus("Saved");
  } catch (err) {
    console.error(err);
    setSaveStatus("Error saving");
  }
}

async function handleSubmitFromQuests(response) {
  if (!response?.id) return;
  try {
    setSaveStatus("Submitting...");
    const wasAlreadySubmitted = response.isSubmitted || response.xpAwarded;
    const xpReward = Number(selectedPrompt?.xpReward || 0);
    await submitWeeklyQuest({
      responseId: response.id,
      userId: currentUser.uid,
      responseText: editingResponseText,
      xpReward,
      promptTitle: selectedPrompt?.title || "",
      promptId: selectedPrompt?.id || "",
      adminUid: selectedPrompt?.createdBy || "",
      userName: currentUser.name || "",
      userAvatar: currentUser.avatar || "",
    });
    const refreshed = await getUserResponseForPrompt(selectedPrompt.id, currentUser.uid);
    setMyResponse(refreshed);
    setEditingResponseId(null);
    setEditingResponseText("");
    const didAwardXp = !wasAlreadySubmitted && xpReward > 0;
    const xpMsg = didAwardXp ? ` +${xpReward} XP` : "";
    setSaveStatus("Submitted!" + xpMsg);
    console.log("[AssignmentsScreen] submit complete", { wasAlreadySubmitted, xpReward, didAwardXp, hasCallback: !!onXpAwarded });
    if (didAwardXp && onXpAwarded) onXpAwarded(xpReward);

    // Fire admin activity event
    writeActivityEvent({
      type: "assignment_submitted",
      userId: currentUser.uid,
      userName: currentUser.name || "",
      userAvatar: currentUser.avatar || "",
      message: `Submitted assignment: "${selectedPrompt?.title || "Weekly Quest"}"${selectedPrompt?.xpReward ? ` (+${selectedPrompt.xpReward} XP)` : ""}`,
      meta: { promptId: selectedPrompt?.id, xpReward: selectedPrompt?.xpReward || 0 },
    });
    const tag = resolvedProgramTag || currentUser.programTag || "";
    const allResponses = await getGroupResponsesForPrompt(selectedPrompt.id, tag);
    const filtered = allResponses.filter((r) => r.userId !== currentUser.uid);
    setGroupResponses(filtered);
    for (const item of filtered) {
      if (item.id) {
        await loadReactionsForTarget("response", item.id);
        if (!item.isPrivate) {
          await loadCommentsForResponse(item.id);
          await loadReactionsForResponseTree(item);
        }
      }
    }
  } catch (err) {
    console.error(err);
    setSaveStatus("Error submitting");
  }
}

async function saveEditedResponse(response) {
  if (!editingResponseText.trim()) return;

  try {
    await updateWeeklyQuestResponse(response.id, {
      responseText: editingResponseText,
    });

    if (response.userId === currentUser.uid) {
      const refreshed = await getUserResponseForPrompt(
        selectedPrompt.id,
        currentUser.uid
      );
      setMyResponse(refreshed);
      await loadCommentsForResponse(refreshed.id);
      await loadReactionsForResponseTree(refreshed);
    } else {
      const tag = resolvedProgramTag || currentUser.programTag || "";
      const allResponses = await getGroupResponsesForPrompt(selectedPrompt.id, tag);
      const filtered = allResponses.filter(
        (item) => item.userId !== currentUser.uid
      );
      setGroupResponses(filtered);

      for (const item of filtered) {
        if (!item.isPrivate && item.id) {
          await loadCommentsForResponse(item.id);
          await loadReactionsForResponseTree(item);
        }
      }
    }

    cancelEditingResponse();
  } catch (error) {
    console.error("Error editing response:", error);
  }
}

function startEditingComment(comment) {
  setEditingCommentId(comment.id);
  setEditingCommentText(comment.text || "");
}

function cancelEditingComment() {
  setEditingCommentId(null);
  setEditingCommentText("");
}

async function saveEditedComment(response, comment) {
  if (!editingCommentText.trim()) return;

  try {
    await updateWeeklyQuestComment(comment.id, {
      text: editingCommentText,
    });

    await loadCommentsForResponse(response.id);
    await loadReactionsForResponseTree(response);
    cancelEditingComment();
  } catch (error) {
    console.error("Error editing comment:", error);
  }
}

async function handleToggleReaction(targetType, targetId, emoji) {
  const key = `${targetType}_${targetId}`;

  // Optimistic update so the reaction appears immediately
  setReactionsByTarget((prev) => {
    const current = prev[key] || [];
    const alreadyReacted = current.some(
      (r) => r.emoji === emoji && r.userId === currentUser.uid
    );
    const updated = alreadyReacted
      ? current.filter((r) => !(r.emoji === emoji && r.userId === currentUser.uid))
      : [...current, { emoji, userId: currentUser.uid, targetType, targetId }];
    return { ...prev, [key]: updated };
  });

  try {
    await toggleWeeklyQuestReaction({ targetType, targetId, emoji, currentUser, postOwnerId: myResponse?.userId || "", promptTitle: selectedPrompt?.title || "" });
    await loadReactionsForTarget(targetType, targetId);
  } catch (error) {
    console.error("Error toggling reaction:", error);
    // Revert optimistic update on failure
    await loadReactionsForTarget(targetType, targetId);
  }
}

function toggleComments(responseId) {
  setExpandedCommentsByResponse((prev) => ({
    ...prev,
    [responseId]: !prev[responseId],
  }));
}

function getCommentCount(responseId) {
  return (commentsByResponse[responseId] || []).length;
}

function renderReactionBar(targetType, targetId) {
  const key = `${targetType}_${targetId}`;
  const reactions = reactionsByTarget[key] || [];
  const emojiOptions = ["❤️", "🔥", "😂", "😮", "🙌", "😭", "🎉"];

  const grouped = emojiOptions
    .map((emoji) => {
      const matches = reactions.filter((reaction) => reaction.emoji === emoji);
      return {
        emoji,
        count: matches.length,
        reactedByMe: matches.some(
          (reaction) => reaction.userId === currentUser.uid
        ),
      };
    })
    .filter((item) => item.count > 0);

  return (
    <div style={{ marginTop: "10px", textAlign: "left" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
        }}
      >
        {grouped.map((item) => (
          <button
            key={item.emoji}
            onClick={() => handleToggleReaction(targetType, targetId, item.emoji)}
            style={{
              padding: "4px 10px",
              borderRadius: "999px",
              border: item.reactedByMe
                ? "2px solid #2563eb"
                : "2px solid #bfdbfe",
              background: item.reactedByMe ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            {item.emoji} {item.count}
          </button>
        ))}

        <button
          onClick={() =>
            setOpenReactionPickerKey((prev) => (prev === key ? null : key))
          }
          style={{
            padding: "4px 10px",
            borderRadius: "999px",
            border: "2px solid #bfdbfe",
            background: "#f8fbff",
            color: "#1e3a5f",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "bold",
          }}
        >
          +
        </button>
      </div>

      {openReactionPickerKey === key ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "8px",
          }}
        >
          {emojiOptions.map((emoji) => (
            <button
              key={emoji}
              onClick={async () => {
                await handleToggleReaction(targetType, targetId, emoji);
                setOpenReactionPickerKey(null);
              }}
              style={{
                padding: "6px 10px",
                borderRadius: "999px",
                border: "2px solid #bfdbfe",
                background: "#f5faff",
                color: "#1e3a5f",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

 async function openPrompt(prompt) {
  setSelectedPrompt(prompt);
  setSaveStatus("Saved");
  setCommentsByResponse({});
  setReactionsByTarget({});
  setExpandedCommentsByResponse({});
  setOpenReactionPickerKey(null);
  cancelEditingResponse();
  cancelEditingComment();

  try {
    const now = new Date();
    const isActive =
      prompt.startAt?.toDate?.() <= now && prompt.endAt?.toDate?.() >= now;

    // For active prompts, ensure response doc exists
    const response = isActive
      ? await ensureWeeklyQuestResponse(prompt, currentUser)
      : await getUserResponseForPrompt(prompt.id, currentUser.uid);

    setMyResponse(response);

    if (response?.id) {
      await loadCommentsForResponse(response.id);
      await loadReactionsForResponseTree(response);
    }

    // Auto-open edit mode for unsubmitted responses
    if (response && !response.isSubmitted) {
      setEditingResponseId(response.id);
      setEditingResponseText(response.responseText || "");
    }

    if (response?.status === "submitted" || isAdmin) {
      const tag = resolvedProgramTag || currentUser.programTag || "";
      const allResponses = isAdmin
        ? await getGroupResponsesForPromptAdmin(prompt.id)
        : await getGroupResponsesForPrompt(prompt.id, tag);

      const filtered = allResponses.filter(
        (item) => item.userId !== currentUser.uid
      );

      setGroupResponses(filtered);

      for (const item of filtered) {
        if (item.id) {
          await loadReactionsForTarget("response", item.id);
          // Admin can see comments on private responses too
          if (!item.isPrivate || isAdmin) {
            await loadCommentsForResponse(item.id);
            await loadReactionsForResponseTree(item);
          }
        }
      }
    } else {
      setGroupResponses([]);
    }
  } catch (error) {
    console.error("Error opening prompt:", error);
    setGroupResponses([]);
  }
}

  function handleCommentDraftChange(responseId, value) {
  setCommentDrafts((prev) => ({
    ...prev,
    [responseId]: value,
  }));
}

async function handleAddComment(response) {
  const draft = commentDrafts[response.id] || "";
  if (!draft.trim()) return;

  try {
    await addWeeklyQuestComment({
      promptId: selectedPrompt.id,
      responseId: response.id,
      postOwnerId: response.userId,
      currentUser,
      text: draft,
      promptTitle: selectedPrompt?.title || "",
    });

    setCommentDrafts((prev) => ({
      ...prev,
      [response.id]: "",
    }));

    await loadCommentsForResponse(response.id);
    await loadReactionsForResponseTree(response);
  } catch (error) {
    console.error("Error adding comment:", error);
  }
}
function formatResponseTime(response) {
  const date =
    response.submittedAt?.toDate?.() ||
    response.updatedAt?.toDate?.() ||
    response.createdAt?.toDate?.();

  if (!date) return "";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function renderCommentsSection(response, canComment) {
  const comments = commentsByResponse[response.id] || [];
  const draft = commentDrafts[response.id] || "";

  return (
    <div
      style={{
        marginTop: "18px",
        paddingTop: "14px",
        borderTop: "1px solid #bfdbfe",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          color: "#1e3a5f",
          fontSize: "13px",
          marginBottom: "12px",
          textAlign: "left",
        }}
      >
        Comments
      </div>

      {comments.length === 0 ? (
        <div
          style={{
            color: "#4b6b8a",
            fontSize: "13px",
            marginBottom: canComment ? "12px" : "0",
            textAlign: "left",
          }}
        >
          No comments yet.
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          {comments.map((comment) => {
            const commentTime = comment.createdAt?.toDate?.()
              ? comment.createdAt.toDate().toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })
              : "";

            const canEditThisComment =
              comment.authorId === currentUser.uid;

            return (
              <div
                key={comment.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  textAlign: "left",
                }}
              >
                {comment.authorAvatar ? (
                  <img
                    src={comment.authorAvatar}
                    alt={comment.authorName}
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "999px",
                      objectFit: "cover",
                      border: "2px solid #bfdbfe",
                      flexShrink: 0,
                      marginTop: "2px",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "999px",
                      background: "#bfdbfe",
                      flexShrink: 0,
                      marginTop: "2px",
                    }}
                  />
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginBottom: "4px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        color: "#1e3a5f",
                        fontSize: "14px",
                        lineHeight: "1.3",
                      }}
                    >
                      {comment.authorName || "User"}
                    </div>

                    {commentTime ? (
                      <div
                        style={{
                          color: "#6b8cb0",
                          fontSize: "12px",
                          lineHeight: "1.3",
                        }}
                      >
                        {commentTime}
                        {comment.isEdited ? " (edited)" : ""}
                      </div>
                    ) : null}
                  </div>

                  {editingCommentId === comment.id ? (
                    <>
                      <RichTextEditor
                        value={editingCommentText}
                        onChange={setEditingCommentText}
                        rows={3}
                      />

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "8px",
                        }}
                      >
                        <button
                          onClick={() => saveEditedComment(response, comment)}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "12px",
                            border: "2px solid #bfdbfe",
                            background: "#f8fbff",
                            color: "#1e3a5f",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          Save
                        </button>

                        <button
                          onClick={cancelEditingComment}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "12px",
                            border: "2px solid #bfdbfe",
                            background: "#f5faff",
                            color: "#1e3a5f",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <RichTextDisplay html={comment.text} style={{ color: "#243b53", fontSize: "14px", lineHeight: "1.55", textAlign: "left" }} />

                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginTop: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        {canEditThisComment ? (
                          <button
                            onClick={() => startEditingComment(comment)}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              color: "#4b6b8a",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: "bold",
                            }}
                          >
                            Edit
                          </button>
                        ) : null}
                      </div>

                      {renderReactionBar("comment", comment.id)}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canComment ? (
        <div style={{ marginTop: "6px" }}>
          <RichTextEditor
            value={draft}
            onChange={(v) => handleCommentDraftChange(response.id, v)}
            placeholder="Write a comment..."
            rows={3}
          />

          <button
            onClick={() => handleAddComment(response)}
            style={{
              marginTop: "10px",
              padding: "10px 14px",
              borderRadius: "14px",
              border: "2px solid #bfdbfe",
              background: "#f8fbff",
              color: "#1e3a5f",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Add Comment
          </button>
        </div>
      ) : null}
    </div>
  );
}



  if (loading) {
    return (
      <div
        style={{
          background: "#f5faff",
          borderRadius: "22px",
          padding: "24px",
          marginTop: "20px",
          border: "2px solid #dbeafe",
          boxShadow: "0 8px 20px rgba(30, 58, 138, 0.10)",
        }}
      >
        Loading assignments...
      </div>
    );
  }

  if (selectedPrompt) {
    const myStatus = myResponse?.status || "not started";
    const hasSubmitted = myResponse?.status === "submitted";

    return (
      <div
        style={{
          background: "#f5faff",
          borderRadius: "22px",
          padding: "24px",
          marginTop: "20px",
          border: "2px solid #dbeafe",
          boxShadow: "0 8px 20px rgba(30, 58, 138, 0.10)",
        }}
      >
        <button
          onClick={() => {
            setSelectedPrompt(null);
            setMyResponse(null);
            setGroupResponses([]);
            loadAssignments();
          }}
          style={{
            marginBottom: "16px",
            padding: "10px 14px",
            borderRadius: "14px",
            border: "2px solid #bfdbfe",
            background: "#f8fbff",
            color: "#1e3a5f",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          ← Back to assignments
        </button>

        <h2 style={{ marginTop: 0, color: "#1e3a5f" }}>{selectedPrompt.title}</h2>

        <RichTextDisplay html={selectedPrompt.prompt} style={{ color: "#4b6b8a", marginBottom: "18px" }} />

        <div
          style={{
            background: "#f8fbff",
            borderRadius: "18px",
            padding: "16px",
            border: "2px solid #bfdbfe",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              color: "#1e3a5f",
              marginBottom: "8px",
            }}
          >
            Your response
          </div>

          <div
            style={{
              fontSize: "13px",
              color: "#4b6b8a",
              marginBottom: "10px",
            }}
          >
            Status: {myStatus}
          </div>

          {myResponse ? (
  <>
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        textAlign: "left",
      }}
    >
      {myResponse.userAvatar ? (
        <img
          src={myResponse.userAvatar}
          alt={myResponse.userName}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "999px",
            objectFit: "cover",
            border: "2px solid #bfdbfe",
            flexShrink: 0,
            marginTop: "2px",
          }}
        />
      ) : currentUser?.avatar ? (
        <img
          src={currentUser.avatar}
          alt={currentUser.name}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "999px",
            objectFit: "cover",
            border: "2px solid #bfdbfe",
            flexShrink: 0,
            marginTop: "2px",
          }}
        />
      ) : (
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "999px",
            background: "#bfdbfe",
            flexShrink: 0,
            marginTop: "2px",
          }}
        />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "6px",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              color: "#1e3a5f",
              fontSize: "15px",
            }}
          >
            {myResponse.userName || currentUser?.name || "You"}
          </div>

          {formatResponseTime(myResponse) ? (
            <div
              style={{
                color: "#6b8cb0",
                fontSize: "12px",
              }}
            >
              {formatResponseTime(myResponse)}
              {myResponse.isEdited ? " (edited)" : ""}
            </div>
          ) : null}
        </div>

        {editingResponseId === myResponse.id ? (
          <>
            <RichTextEditor
              value={editingResponseText}
              onChange={setEditingResponseText}
              rows={8}
            />

            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "10px",
                flexWrap: "wrap",
              }}
            >
              {!hasSubmitted ? (
                <>
                  <button
                    onClick={() => saveDraft(myResponse)}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "14px",
                      border: "2px solid #bfdbfe",
                      background: "#f8fbff",
                      color: "#1e3a5f",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    Save Draft
                  </button>
                  <button
                    onClick={() => handleSubmitFromQuests(myResponse)}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "14px",
                      border: "2px solid #2563eb",
                      background: "#e0f2fe",
                      color: "#1e3a5f",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => saveEditedResponse(myResponse)}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "14px",
                      border: "2px solid #bfdbfe",
                      background: "#f8fbff",
                      color: "#1e3a5f",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEditingResponse}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "14px",
                      border: "2px solid #bfdbfe",
                      background: "#f5faff",
                      color: "#1e3a5f",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <RichTextDisplay html={myResponse.responseText || ""} style={{ color: "#243b53", textAlign: "left", lineHeight: "1.6", marginTop: "2px", marginBottom: "6px" }} />

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "8px",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => startEditingResponse(myResponse)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#4b6b8a",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "bold",
                }}
              >
                Edit
              </button>
            </div>

            {renderReactionBar("response", myResponse.id)}
          </>
        )}

        <div
          style={{
            marginTop: "10px",
            fontSize: "12px",
            color: "#4b6b8a",
            textAlign: "left",
          }}
        >
          {saveStatus}
        </div>

        {myResponse.isPrivate ? (
          <div
            style={{
              marginTop: "10px",
              fontSize: "13px",
              color: "#4b6b8a",
              fontStyle: "italic",
              textAlign: "left",
            }}
          >
            This response is private to coach only.
          </div>
        ) : null}

<div
  style={{
    display: "flex",
    gap: "12px",
    marginTop: "10px",
    flexWrap: "wrap",
  }}
>
  <button
    onClick={() => toggleComments(myResponse.id)}
    style={{
      background: "none",
      border: "none",
      padding: 0,
      color: "#4b6b8a",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "bold",
    }}
  >
    {getCommentCount(myResponse.id) > 0
      ? `${getCommentCount(myResponse.id)} comment${
          getCommentCount(myResponse.id) === 1 ? "" : "s"
        }`
      : "Comments"}
  </button>
</div>

{expandedCommentsByResponse[myResponse.id] ? (
  renderCommentsSection(myResponse, true)
) : null}      </div>
    </div>
  </>
) : (
  <div style={{ color: "#4b6b8a" }}>No response yet.</div>
)}
        </div>

        {(hasSubmitted || isAdmin) ? (
          <div
            style={{
              background: "#f8fbff",
              borderRadius: "18px",
              padding: "16px",
              border: "2px solid #bfdbfe",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                color: "#1e3a5f",
                marginBottom: "12px",
              }}
            >
              {isAdmin ? "All Responses" : "Group responses"}
            </div>

            {groupResponses.length === 0 ? (
              <div style={{ color: "#4b6b8a" }}>
                No other responses yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: "14px" }}>
                {groupResponses.map((response) => (
  <div
    key={response.id}
    style={{
      background: "#f5faff",
      borderRadius: "16px",
      padding: "16px",
      border: "2px solid #dbeafe",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        textAlign: "left",
      }}
    >
      {response.userAvatar ? (
        <img
          src={response.userAvatar}
          alt={response.userName}
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "999px",
            objectFit: "cover",
            border: "2px solid #bfdbfe",
            flexShrink: 0,
            marginTop: "2px",
          }}
        />
      ) : (
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "999px",
            background: "#bfdbfe",
            flexShrink: 0,
            marginTop: "2px",
          }}
        />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "8px",
            flexWrap: "wrap",
            marginBottom: "6px",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              color: "#1e3a5f",
              fontSize: "15px",
            }}
          >
            {response.userName || "User"}
          </div>

          {formatResponseTime(response) ? (
            <div
              style={{
                color: "#6b8cb0",
                fontSize: "12px",
              }}
            >
              {formatResponseTime(response)}
              {response.isEdited ? " (edited)" : ""}
            </div>
          ) : null}

          {response.isPrivate && isAdmin ? (
            <div style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: "999px",
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              color: "#92400e",
              fontSize: "11px",
              fontWeight: "bold",
              marginTop: "2px",
            }}>
              🔒 Private — coach only
            </div>
          ) : null}
        </div>

        {response.isPrivate && !isAdmin ? (
          <>
            <div
              style={{
                color: "#4b6b8a",
                fontStyle: "italic",
                textAlign: "left",
                lineHeight: "1.5",
              }}
            >
              Submitted a private response to coach.
            </div>

            {renderReactionBar("response", response.id)}
          </>
        ) : editingResponseId === response.id ? (
          <>
            <RichTextEditor
              value={editingResponseText}
              onChange={setEditingResponseText}
              rows={6}
            />

            <div
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "10px",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => saveEditedResponse(response)}
                style={{
                  padding: "10px 14px",
                  borderRadius: "14px",
                  border: "2px solid #bfdbfe",
                  background: "#f8fbff",
                  color: "#1e3a5f",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Save
              </button>

              <button
                onClick={cancelEditingResponse}
                style={{
                  padding: "10px 14px",
                  borderRadius: "14px",
                  border: "2px solid #bfdbfe",
                  background: "#f5faff",
                  color: "#1e3a5f",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <RichTextDisplay html={response.responseText || ""} style={{ color: "#243b53", textAlign: "left", lineHeight: "1.6", marginTop: "2px", marginBottom: "6px" }} />

        

            {renderReactionBar("response", response.id)}
<div
  style={{
    display: "flex",
    gap: "12px",
    marginTop: "10px",
    flexWrap: "wrap",
  }}
>
  <button
    onClick={() => toggleComments(response.id)}
    style={{
      background: "none",
      border: "none",
      padding: 0,
      color: "#4b6b8a",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "bold",
    }}
  >
    {getCommentCount(response.id) > 0
      ? `${getCommentCount(response.id)} comment${
          getCommentCount(response.id) === 1 ? "" : "s"
        }`
      : "Comments"}
  </button>
</div>

{expandedCommentsByResponse[response.id] ? (
  renderCommentsSection(response, true)
) : null}          </>
        )}
      </div>
    </div>
  </div>
))}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              color: "#4b6b8a",
              fontSize: "13px",
            }}
          >
            Group responses unlock after you submit your own response.
          </div>
        )}
      </div>
    );
  }

  function renderPromptCard(prompt) {
    const status = prompt.myResponse?.status || "not started";
    const startDate = prompt.startAt?.toDate?.();
    const endDate = prompt.endAt?.toDate?.();
    const now = new Date();
    const isActive = startDate && endDate && startDate <= now && endDate >= now;
    const groupResponseCount = prompt.groupResponseCount || 0;

    const formattedDateRange =
      startDate && endDate
        ? `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
        : "";

    const statusColors = {
      submitted: { bg: "#e0f2fe", border: "#7dd3fc", dot: "#2563eb", label: "Submitted" },
      draft:     { bg: "#f8fbff", border: "#bfdbfe", dot: "#38bdf8", label: "In Progress" },
      missed:    { bg: "#f8fbff", border: "#bfdbfe", dot: "#d1c4e9", label: "Missed" },
    };
    const sc = statusColors[status] || { bg: "#f8fbff", border: "#bfdbfe", dot: "#93c5fd", label: isActive ? "Open" : "Upcoming" };

    return (
      <button
        key={prompt.id}
        onClick={() => openPrompt(prompt)}
        style={{
          textAlign: "left",
          padding: "16px",
          borderRadius: "18px",
          border: `2px solid ${sc.border}`,
          background: sc.bg,
          cursor: "pointer",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
          <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", flex: 1 }}>
            {prompt.title}
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: "5px", flexShrink: 0,
            fontSize: "12px", fontWeight: "bold", color: "#4b6b8a",
          }}>
            <span style={{ width: "7px", height: "7px", borderRadius: "999px", background: sc.dot, display: "inline-block" }} />
            {sc.label}
          </div>
        </div>
        <div style={{ display: "flex", gap: "12px", marginTop: "6px", flexWrap: "wrap" }}>
          {formattedDateRange && (
            <span style={{ fontSize: "12px", color: "#6b8cb0" }}>{formattedDateRange}</span>
          )}
          {status === "submitted" && groupResponseCount > 0 && (
            <span style={{ fontSize: "12px", color: "#6b8cb0" }}>
              {groupResponseCount} group response{groupResponseCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </button>
    );
  }

  // Sort: active first, then by start date desc
  const sortedPrompts = [...prompts].sort((a, b) => {
    const now = new Date();
    const aActive = a.startAt?.toDate?.() <= now && a.endAt?.toDate?.() >= now;
    const bActive = b.startAt?.toDate?.() <= now && b.endAt?.toDate?.() >= now;
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return (b.startAt?.toDate?.()?.getTime?.() || 0) - (a.startAt?.toDate?.()?.getTime?.() || 0);
  });

  const visiblePrompts = showAllPrompts ? sortedPrompts : sortedPrompts.slice(0, 1);
  const hiddenCount = sortedPrompts.length - 1;

  return (
    <div style={{ display: "grid", gap: "10px" }}>
      {prompts.length === 0 ? (
        <div style={{ color: "#4b6b8a", fontSize: "14px" }}>No assignments yet.</div>
      ) : (
        <>
          {visiblePrompts.map((prompt) => renderPromptCard(prompt))}
          {sortedPrompts.length > 1 && (
            <button
              onClick={() => setShowAllPrompts((p) => !p)}
              style={{
                padding: "10px",
                borderRadius: "14px",
                border: "2px solid #bfdbfe",
                background: "#f5faff",
                color: "#4b6b8a",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {showAllPrompts ? "▲ Show less" : `▼ Show ${hiddenCount} older assignment${hiddenCount === 1 ? "" : "s"}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}